import { useContext, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { useSession } from '../session/context';
import { StudioFileDropContext } from '../session/useWindowFileDrop';
import { blankDocument } from '../session/blankDocument';
import { useLicense } from '../license/context';
import { UpgradePrompt } from '../license/UpgradePrompt';
import { isFirstRun, markOnboarded, sampleFile } from '../onboarding';
import { TranscribeButton } from '../transcribe/TranscribeButton';
import './EmptyState.css';

/** Studio's empty state (title + orientation), the only mode that mounts it. */
const EMPTY_TITLE = 'Studio';
const EMPTY_LEAD =
  'Open a subtitle file to edit cues, run compliance QC, and retime — then load a video (Pro) to work against it.';

/**
 * Studio's empty state (SP5, cue-forge-9ww.2). Instead of dead-ending with "open
 * a file in Convert first", Studio can load a file (drop or pick) or try a
 * bundled sample right here. A first-run line reassures new users that nothing is
 * uploaded. All local. (After the 5→2 mode collapse this is only ever mounted by
 * Studio, so the old per-mode `mode` prop + lead map are gone — cue-forge-zxw.10.)
 *
 * Studio is the default hero for a bare /app/, so a drop here must never silently
 * no-op (cue-forge-zxw.4). We thread the license in: `load` gets `isPro` so a
 * licensed user's broadcast-format drop (TTML/iTT/SCC/STL) is accepted, and the
 * session's `error` / `proImportLocked` states are surfaced right here — a parse
 * error, or a locked-Pro upgrade prompt for free users — mirroring the Converter.
 * (`load`'s `allowPro` default stays false; the caller passes it.)
 */
export function EmptyState() {
  const { load, loadFreshDocument, error, proImportLocked } = useSession();
  const { isPro } = useLicense();
  // Studio's shared drop intake (the shell's window-net handler): a multi-file
  // drop on THIS dropzone must behave exactly like one a few pixels outside it —
  // load the first file and report the ignored rest via the shell's one drop
  // notice, never a silent files[1..] discard. Null without the shell (tests).
  const studioDrop = useContext(StudioFileDropContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [firstRun, setFirstRun] = useState(isFirstRun);

  function doLoad(file: File): void {
    markOnboarded();
    setFirstRun(false);
    void load(file, isPro);
  }

  // "Start from scratch" (cue-forge-wwy.2): a blank document with no source file
  // at all — a free counterpart to the transcription EMPTY-START path, so an
  // operator can author cues by hand from nothing via the Insert-cue affordance.
  function startFromScratch(): void {
    markOnboarded();
    setFirstRun(false);
    loadFreshDocument(blankDocument());
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (studioDrop) {
      markOnboarded();
      setFirstRun(false);
      studioDrop(files);
      return;
    }
    doLoad(files[0]!);
  }

  return (
    <section className="cf-empty">
      <h1 className="cf-empty__title">{EMPTY_TITLE}</h1>
      <p className="cf-empty__lead">{EMPTY_LEAD}</p>
      <div
        className={`cf-dropzone${dragOver ? ' cf-dropzone--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <p className="cf-dropzone__hint">Drop a subtitle file here, or</p>
        <div className="cf-empty__actions">
          <button type="button" className="cf-btn" onClick={() => inputRef.current?.click()}>
            Open file
          </button>
          <button
            type="button"
            className="cf-btn cf-btn--primary"
            onClick={() => doLoad(sampleFile())}
          >
            Try a sample
          </button>
          <button type="button" className="cf-btn" onClick={startFromScratch}>
            Start from scratch
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="cf-visually-hidden"
          aria-label="Choose a subtitle file"
          accept=".srt,.vtt,.sbv,.ass,.ssa,.scc,.stl,.ttml,.dfxp,.xml,.itt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) doLoad(file);
            e.target.value = '';
          }}
        />
      </div>
      {/* On-device Whisper transcription (cue-forge-sdv.11.4) as a way to start
          with no subtitle file at all — the app's first doc-creating feature
          (EMPTY-START in TranscribeDialog). Pro-gated inline like everywhere
          else the trigger appears (Studio toolbar). */}
      <p className="cf-empty__or">or transcribe audio/video to create a fresh subtitle file</p>
      <TranscribeButton />
      {firstRun && (
        <p className="cf-empty__welcome">
          New to CueForge? Everything runs in your browser. Your files never leave this device.
        </p>
      )}

      {/* Never a silent drop (cue-forge-zxw.4): a genuine parse failure shows an
          error; a free user's Pro-format drop shows the locked-Pro upgrade path. */}
      {error && (
        <p className="cf-empty__error" role="alert">
          {error}
        </p>
      )}
      {proImportLocked && <UpgradePrompt feature={`${proImportLocked} import`} surface="import" />}
    </section>
  );
}
